import { currentTenantCode } from '@/utils/tenant'

/**
 * The last answer of a list, kept on this device so the list stands at once when it
 * opens again; the fresh answer replaces it a moment later. Per tenant, user and request,
 * dropped after 20 hours and on signing out. Too big an answer (the hub over a long
 * period) is not kept: the list then shows its placeholders, as on the first opening.
 */

const PREFIX = 'snapshot:v1:'
/** Characters per answer at most; localStorage holds some five million per site. */
const MAX_LENGTH = 1_500_000
// Less than a day: the previews' signed links in an answer hold a day
const MAX_AGE_MS = 20 * 60 * 60 * 1000

/** The request in a fixed order, without the parts not sent. */
function stable(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stable)
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.keys(value)
        .filter((key) => (value as Record<string, unknown>)[key] !== undefined)
        .sort()
        .map((key) => [key, stable((value as Record<string, unknown>)[key])]),
    )
  }
  return value
}

/** Where a list's answer to one request is kept. */
export function snapshotKey(name: string, userId: number | string | undefined, request: unknown) {
  return `${PREFIX}${currentTenantCode() ?? '-'}:${userId ?? 'anon'}:${name}:${JSON.stringify(stable(request))}`
}

/** The answer kept for the request, or null (none, too old, or no storage). */
export function readSnapshot<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const { at, value } = JSON.parse(raw) as { at: number; value: T }
    if (Date.now() - at > MAX_AGE_MS) {
      localStorage.removeItem(key)
      return null
    }
    return value
  } catch {
    return null
  }
}

/** All kept answers together at most, so the site's own settings always find room. */
const MAX_TOTAL = 3_000_000

/** When an answer was kept, read off the start of its text without parsing all of it. */
function keptAt(raw: string): number {
  return Number(/^\{"at":(\d+)/.exec(raw)?.[1] ?? 0)
}

/**
 * Drop the answers past their age and, oldest first, as many as it takes to leave room
 * for `length` more within {@link MAX_TOTAL}. Without this, every search word and page
 * would stay until signing out.
 */
function makeRoom(length: number, replaced: string) {
  const now = Date.now()
  const kept = Object.keys(localStorage)
    .filter((key) => key.startsWith(PREFIX) && key !== replaced)
    .map((key) => {
      const raw = localStorage.getItem(key) ?? ''
      return { key, at: keptAt(raw), length: raw.length }
    })
    .sort((a, b) => a.at - b.at)
  let total = kept.reduce((sum, entry) => sum + entry.length, length)
  for (const entry of kept) {
    if (now - entry.at <= MAX_AGE_MS && total <= MAX_TOTAL) continue
    localStorage.removeItem(entry.key)
    total -= entry.length
  }
}

/** Keep the answer for the next opening; too big, or no room left, it is not kept. */
export function keepSnapshot(key: string, value: unknown) {
  try {
    const raw = JSON.stringify({ at: Date.now(), value })
    if (raw.length > MAX_LENGTH) {
      localStorage.removeItem(key)
      return
    }
    makeRoom(raw.length, key)
    try {
      localStorage.setItem(key, raw)
    } catch {
      // the site's other entries fill it: the kept answers give way, once
      clearSnapshots()
      localStorage.setItem(key, raw)
    }
  } catch {
    // storage full or blocked: the list loads as it did
  }
}

/**
 * Forget what one load (`name`) kept, for every request: after a change it would show
 * the change undone (a deleted plan back in the list).
 */
export function forgetSnapshots(name: string) {
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(PREFIX) && key.includes(`:${name}:`))
      .forEach((key) => localStorage.removeItem(key))
  } catch {
    // no storage
  }
}

/** Forget every kept answer: on signing out, so the next user never sees them. */
export function clearSnapshots() {
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(PREFIX))
      .forEach((key) => localStorage.removeItem(key))
  } catch {
    // no storage
  }
}
